import { booleanAttribute, ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  DsBoxModule,
  DsButtonModule,
  DsMessageModule,
  DsSwitchModule,
  DsToggleButtonModule,
  DsTooltipDirectiveModule,
} from '@bmw-ds/components';

import { CustomerVehicle } from '../../../customer/model/customer-vehicle.model';
import { Customer } from '../../../customer/model/customer.model';
import { useI18n } from '../../../i18n/services/i18n.service';
import { ButtonRowComponent } from '../../../modal/button/button-row.component';
import { VehicleBoxComponent } from '../vehicle-box/vehicle-box.component';
import { RemoveRelation, VehicleFeatures } from './vehicles.view-model';

@Component({
  selector: 'cdf-vehicles',
  imports: [
    DsBoxModule,
    VehicleBoxComponent,
    DsMessageModule,
    DsButtonModule,
    DsToggleButtonModule,
    FormsModule,
    DsSwitchModule,
    DsTooltipDirectiveModule,
    ButtonRowComponent,
  ],
  template: `
    <ds-box elevation="base">
      <ds-box-content>
        <div class="d-flex flex-row mb-4x gap-2x">
          <h2>{{ i18n('vehicles.heading') }}</h2>
          @if (canUseEditMode()) {
            <cdf-button-row class="ms-auto">
              @if (isEditMode() && features().addVehicles) {
                <button ds-button icon="add" variant="outline" (click)="showAddVehicleModal.emit()">
                  {{ i18n('vehicles.addVehicle') }}
                </button>
              }
              <button
                ds-button
                [icon]="isEditMode() ? 'check' : 'edit'"
                [variant]="isEditMode() ? 'primary' : 'outline'"
                [disabled]="readonly()"
                [ds-tooltip]="readonlyTooltip()"
                (click)="isEditMode.set(!isEditMode())"
              >
                {{ i18n(isEditMode() ? 'button.done' : 'button.edit') }}
              </button>
            </cdf-button-row>
          }
        </div>
        @if (hasVehicles()) {
          <div class="vehicle-boxes" [class.has-button-column]="isEditMode() && features().removeVehicles">
            @for (vehicle of customer().vehicles; track vehicle.vin) {
              <cdf-vehicle-box
                [vehicle]="vehicle"
                [activeCountry]="activeCountry()"
                [enableSelection]="!isEditMode() && enableVehicleSelection()"
                (selectVehicle)="selectVehicle.emit(vehicle.vin)"
                (removeRelation)="showRemoveRelationModal.emit({ vehicle, relation: $event })"
                (removeVehicle)="showRemoveVehicleModal.emit(vehicle)"
                [canRemoveVehicle]="isEditMode() && features().removeVehicles"
                [canRemoveRelations]="isEditMode() && features().removeRelations"
                [canAddRelations]="isEditMode() && features().addRelations"
                [canEditLicensePlate]="isEditMode() && features().editLicensePlate"
                (addRelation)="showAddRelationModal.emit(vehicle)"
                (editLicensePlate)="showEditLicensePlateModal.emit(vehicle)"
              />
            }
          </div>
        } @else {
          <ds-message tone="info" [messageText]="i18n('vehicles.noneFound')" />
        }
      </ds-box-content>
    </ds-box>
  `,
  styleUrl: './vehicles.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VehiclesComponent {
  readonly i18n = useI18n();

  readonly customer = input.required<Customer>();
  readonly activeCountry = input<string | undefined>();
  readonly loading = input(false);
  readonly readonly = input(false, { transform: booleanAttribute });
  readonly features = input.required<VehicleFeatures>();
  readonly enableVehicleSelection = input(false, { transform: booleanAttribute });

  readonly selectVehicle = output<string>();

  protected readonly readonlyTooltip = computed(() => {
    if (this.readonly()) {
      return this.i18n('readonlyTooltip');
    }
    return '';
  });

  readonly isEditMode = signal(false);
  readonly hasVehicles = computed(() => (this.customer().vehicles?.length ?? 0) > 0);

  protected readonly canUseEditMode = computed(
    () =>
      this.features().addVehicles ||
      (this.hasVehicles() &&
        (this.features().removeRelations ||
          this.features().removeVehicles ||
          this.features().addRelations ||
          this.features().editLicensePlate)),
  );

  readonly showRemoveRelationModal = output<RemoveRelation>();
  readonly showRemoveVehicleModal = output<CustomerVehicle>();
  readonly showAddRelationModal = output<CustomerVehicle>();
  readonly showAddVehicleModal = output();
  readonly showEditLicensePlateModal = output<CustomerVehicle>();
}
